import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Loader2, Pencil, Link2, Users, Check, LifeBuoy, Save, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { getExchange } from "@/data/exchanges";
import { cn } from "@/lib/utils";

function Perm({ label, on }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#A3A3A3]">{label}</span>
      <span className={cn("w-5 h-5 rounded-sm border flex items-center justify-center", on ? "bg-[#10B981] border-[#10B981]" : "border-[#333333]")}>
        {on && <Check className="w-3.5 h-3.5 text-[#0A0A0A]" strokeWidth={3} />}
      </span>
    </div>
  );
}

export default function ClientManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [intro, setIntro] = useState("");

  const { data: c, isLoading } = useQuery({ queryKey: ["partner-client", id], queryFn: async () => (await api.get(`/partner/clients/${id}`)).data });

  // setName/setIntro are stable state setters; sync only when the loaded client `c` changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (c) { setName(c.account_name || ""); setIntro(c.introduction || ""); } }, [c]);

  const save = async () => {
    try {
      await api.patch(`/partner/clients/${id}`, { account_name: name, introduction: intro });
      qc.invalidateQueries({ queryKey: ["partner-client", id] });
      qc.invalidateQueries({ queryKey: ["partner-clients"] });
      setEditing(false);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (isLoading || !c) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>;

  const ex = getExchange(c.exchange_slug);

  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-[#737373] mb-2">
        <button onClick={() => navigate("/partner/clients")} className="hover:text-[#A3A3A3]">Home</button>
        <ChevronRight className="w-3 h-3" />
        <button onClick={() => navigate("/partner/clients")} className="hover:text-[#A3A3A3]">My clients</button>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#A3A3A3]">Manage Account</span>
      </div>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-[#F5F5F5] mb-6" data-testid="manage-account-title">Manage Account</h1>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Profile */}
        <div className="lg:col-span-2 rounded-lg border border-[#1f1f1f] bg-[#0D0D0D] p-6" data-testid="client-profile-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-heading font-semibold text-[#F5F5F5]">Profile</span>
            </div>
            {editing ? (
              <div className="flex items-center gap-2">
                <button onClick={save} data-testid="client-save-btn" className="w-8 h-8 rounded-md border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center hover:bg-[rgba(212,175,55,0.1)]"><Save className="w-4 h-4" /></button>
                <button onClick={() => { setEditing(false); setName(c.account_name); setIntro(c.introduction); }} className="w-8 h-8 rounded-md border border-[#333333] text-[#A3A3A3] flex items-center justify-center hover:text-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} data-testid="client-edit-btn" className="w-8 h-8 rounded-md border border-[#333333] text-[#A3A3A3] flex items-center justify-center hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"><Pencil className="w-4 h-4" /></button>
            )}
          </div>

          <div className="flex items-center gap-4 mb-5">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-[#1A1A1A] border border-[#333333] flex items-center justify-center"><Users className="w-6 h-6 text-[#737373]" /></div>
              <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[#0D0D0D] border-2 border-[#141414] flex items-center justify-center font-heading font-bold text-[10px]" style={{ color: ex.color }}>{ex.name.slice(0, 2).toUpperCase()}</span>
            </div>
            <div>
              {editing ? (
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="client-name-input"
                  className="bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-1.5 text-lg text-[#F5F5F5] font-heading font-semibold focus:outline-none focus:border-[#D4AF37]" />
              ) : (
                <div className="font-heading text-lg font-semibold text-[#F5F5F5]">{c.account_name}</div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm">{c.connected ? "Connected" : "Disconnected"}</span>
                <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm capitalize">{c.market}</span>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-[#1f1f1f] bg-[#0A0A0A] p-4 min-h-[180px]">
            {editing ? (
              <textarea value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Add an introduction for this client..." data-testid="client-intro-input"
                className="w-full h-40 bg-transparent text-sm text-[#F5F5F5] placeholder:text-[#525252] resize-none focus:outline-none" />
            ) : c.introduction ? (
              <p className="text-sm text-[#A3A3A3] leading-relaxed whitespace-pre-wrap">{c.introduction}</p>
            ) : (
              <div className="h-40 flex items-center justify-center text-sm text-[#525252]">No introduction available</div>
            )}
          </div>
        </div>

        {/* API */}
        <div className="rounded-lg border border-[#1f1f1f] bg-[#0D0D0D] p-6" data-testid="client-api-card">
          <div className="flex items-center gap-2 mb-5">
            <Link2 className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-heading font-semibold text-[#F5F5F5]">API</span>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3]">Status:</span>
              <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-sm", c.status === "active" ? "bg-[rgba(16,185,129,0.15)] text-[#10B981]" : "bg-[#1A1A1A] text-[#737373]")}>{c.status === "active" ? "Active" : "Inactive"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3]">Key:</span>
              <span className="font-mono text-sm text-[#F5F5F5]">{c.api_key_masked}</span>
            </div>
            <div className="pt-1">
              <div className="text-sm text-[#A3A3A3] mb-2">Permissions:</div>
              <div className="space-y-2.5">
                <Perm label="Read" on={c.permissions?.read} />
                <Perm label="Trade" on={c.permissions?.trade} />
                {c.permissions?.withdraw && <Perm label="Withdraw" on />}
              </div>
            </div>
          </div>
        </div>

        {/* Assistance */}
        <div className="rounded-lg border border-[#1f1f1f] bg-[#0D0D0D] p-6 flex flex-col items-center justify-center text-center" data-testid="client-assistance-card">
          <div className="w-11 h-11 rounded-md bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.2)] flex items-center justify-center mb-3">
            <LifeBuoy className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="font-heading font-semibold text-[#F5F5F5]">Need Assistance?</div>
          <p className="text-sm text-[#A3A3A3] mt-2">If you need any help, please feel free to reach out to us anytime!</p>
        </div>
      </div>
    </div>
  );
}
